// Billing for company staff (FR-46, BL-02, BL-05; any role may view): the plan
// and prepaid credit with M-Pesa Paybill top-up instructions, charged calls per
// day for a month with its CSV export, and invoices with their PDFs. When the
// platform has no billing, the credit endpoint answers 404 and the tab says so.
import {
  Alert,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  NativeSelect,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorCode, errorMessage, unwrap } from "../../shared/session/errors";
import { saveBlob } from "../../shared/ui/download";
import { useFormat } from "../../shared/ui/format";
import { BILLING_OFFSET_MS, billingMonths, monthRange, type Month } from "../../shared/ui/months";
import { FormError } from "../../shared/ui/Page";
import { companyKey } from "./common";

export { billingMonths, monthRange };

type Credit = Schemas["CompanyCredit"];
type UsageDay = Schemas["UsageDay"];
type Invoice = Schemas["Invoice"];

const INVOICE_COLOUR: Record<Invoice["status"], string> = {
  issued: "blue",
  paid: "green",
  overdue: "red",
  void: "gray",
};

export function BillingTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const credit = useQuery({
    queryKey: companyKey(companyId, "billing", "credit"),
    queryFn: () => unwrap(api.GET("/companies/{company_id}/credit", { params: { path: { company_id: companyId } } })),
  });

  if (credit.isPending) return <Loader size="sm" aria-label={t("common.loading")} />;
  if (credit.isError) {
    if (errorCode(credit.error) === "not_found") {
      return (
        <Paper withBorder p="md" radius="md" data-testid="billing-disabled">
          <Stack gap={4}>
            <Title order={2} size="h4">
              {t("billing.disabled_title")}
            </Title>
            <Text size="sm" c="dimmed">
              {t("billing.disabled_body")}
            </Text>
          </Stack>
        </Paper>
      );
    }
    return <FormError message={errorMessage(credit.error, t("error.generic"))} />;
  }
  return (
    <Stack gap="lg">
      <CreditSection credit={credit.data} />
      <UsageSection companyId={companyId} />
      <InvoicesSection companyId={companyId} />
    </Stack>
  );
}

function CreditSection({ credit }: { credit: Credit }) {
  const { t } = useTranslation();
  const f = useFormat();
  const prepaid = credit.plan === "prepaid";
  return (
    <Paper withBorder p="md" radius="md" data-testid="billing-credit">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={2} size="h4">
            {t("billing.credit_title")}
          </Title>
          <Group gap="xs">
            <Badge color="gray" variant="light" data-testid="billing-plan">
              {t(`billing.plan_${credit.plan}`)}
            </Badge>
            <Badge color={credit.blocked ? "red" : "green"} data-testid="billing-availability">
              {credit.blocked ? t("billing.calls_paused") : t("billing.calls_allowed")}
            </Badge>
          </Group>
        </Group>
        {prepaid && (
          <Stack gap={0}>
            <Text size="sm" c="dimmed">
              {t("billing.balance")}
            </Text>
            <Text size="xl" fw={700} data-testid="billing-balance">
              {f.money(credit.balance_minor, credit.currency)}
            </Text>
          </Stack>
        )}
        {credit.blocked ? (
          <Alert color="red" role="alert" title={t("billing.blocked_title")} data-testid="billing-blocked">
            {prepaid ? t("billing.blocked_prepaid") : t("billing.blocked_postpaid")}
          </Alert>
        ) : (
          prepaid &&
          credit.low_credit && (
            <Alert color="yellow" title={t("billing.low_title")} data-testid="billing-low-credit">
              {t("billing.low_body")}
            </Alert>
          )
        )}
        <Title order={3} size="h5">
          {prepaid ? t("billing.topup_title") : t("billing.pay_title")}
        </Title>
        <Text size="sm" data-testid="billing-paybill">
          {t("billing.paybill", { paybill: credit.paybill, account: credit.account_reference })}
        </Text>
        <Text size="sm" c="dimmed">
          {prepaid ? t("billing.topup_hint") : t("billing.pay_hint")}
        </Text>
        <CopyValue label={t("billing.paybill_number")} value={credit.paybill} />
        <CopyValue label={t("billing.account_number")} value={credit.account_reference} />
      </Stack>
    </Paper>
  );
}

function CopyValue({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <Group gap="xs" wrap="wrap">
      <Text size="sm" fw={600} miw={140}>
        {label}
      </Text>
      <Code fz="md" style={{ wordBreak: "break-all" }}>
        {value}
      </Code>
      <CopyButton value={value} timeout={3000}>
        {({ copied, copy }) => (
          <Button
            size="xs"
            variant="light"
            onClick={copy}
            aria-label={copied ? t("common.copied") : t("secret.copy_label", { label })}
          >
            {copied ? t("common.copied") : t("common.copy")}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}

function UsageSection({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const months = useMemo(() => billingMonths(), []);
  const [selected, setSelected] = useState(months[0]?.value ?? "");
  const month = months.find((m) => m.value === selected) ?? months[0];
  const range = month ? monthRange(month) : { from: "" };
  const usage = useQuery({
    queryKey: companyKey(companyId, "billing", "usage", selected),
    queryFn: () =>
      unwrap(
        api.GET("/companies/{company_id}/usage", {
          params: { path: { company_id: companyId }, query: range },
        }),
      ),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const monthLabel = (m: Month) =>
    new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(Date.UTC(m.year, m.month, 1)),
    );
  const items = [...(usage.data?.items ?? [])].sort(
    (a, b) => a.day.localeCompare(b.day) || a.kind.localeCompare(b.kind),
  );
  const currency = usage.data?.currency ?? "";
  const totalCount = items.reduce((n, u) => n + u.count, 0);
  const totalAmount = items.reduce((n, u) => n + u.amount_minor, 0);
  const kindLabel = (k: UsageDay["kind"]) => t(`usage_kind.${k}`);

  const download = async () => {
    setError(null);
    setBusy(true);
    const { data, error } = await api.GET("/companies/{company_id}/usage/export", {
      params: { path: { company_id: companyId }, query: range },
      parseAs: "blob",
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    saveBlob("usage.csv", data);
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="billing-usage">
      <Stack gap="sm">
        <Title order={2} size="h4">
          {t("billing.usage_title")}
        </Title>
        <Group align="flex-end" gap="sm">
          <NativeSelect
            label={t("billing.month")}
            value={selected}
            onChange={(e) => setSelected(e.currentTarget.value)}
            data={months.map((m) => ({ value: m.value, label: monthLabel(m) }))}
          />
          <Button variant="light" loading={busy} onClick={() => void download()}>
            {t("billing.download_csv")}
          </Button>
        </Group>
        <Text size="sm" c="dimmed">
          {t("billing.csv_hint")}
        </Text>
        <FormError message={error} />
        {usage.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : usage.isError ? (
          <FormError message={errorMessage(usage.error, t("error.generic"))} />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="usage-empty">
            {t("billing.usage_empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={480}>
            <Table verticalSpacing="xs" aria-label={t("billing.usage_title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("billing.day")}</Table.Th>
                  <Table.Th>{t("billing.kind")}</Table.Th>
                  <Table.Th ta="right">{t("billing.calls")}</Table.Th>
                  <Table.Th ta="right">{t("billing.amount")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((u) => (
                  <Table.Tr key={`${u.day}-${u.kind}`} data-testid="usage-row">
                    <Table.Td>{f.day(u.day)}</Table.Td>
                    <Table.Td>{kindLabel(u.kind)}</Table.Td>
                    <Table.Td ta="right">{u.count.toLocaleString(i18n.language)}</Table.Td>
                    <Table.Td ta="right">{f.money(u.amount_minor, currency)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              <Table.Tfoot>
                <Table.Tr data-testid="usage-total">
                  <Table.Th colSpan={2}>{t("billing.total")}</Table.Th>
                  <Table.Th ta="right">{totalCount.toLocaleString(i18n.language)}</Table.Th>
                  <Table.Th ta="right">{f.money(totalAmount, currency)}</Table.Th>
                </Table.Tr>
              </Table.Tfoot>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}

function InvoicesSection({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const invoices = useQuery({
    queryKey: companyKey(companyId, "billing", "invoices"),
    queryFn: () => unwrap(api.GET("/companies/{company_id}/invoices", { params: { path: { company_id: companyId } } })),
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const items = invoices.data?.items ?? [];
  const period = (inv: Invoice) =>
    new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(Date.parse(inv.period_start) + BILLING_OFFSET_MS),
    );

  const download = async (inv: Invoice) => {
    setError(null);
    setBusy(inv.id);
    const { data, error } = await api.GET("/companies/{company_id}/invoices/{invoice_id}/pdf", {
      params: { path: { company_id: companyId, invoice_id: inv.id } },
      parseAs: "blob",
    });
    setBusy(null);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    saveBlob(`${inv.number}.pdf`, data);
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="billing-invoices">
      <Stack gap="sm">
        <Title order={2} size="h4">
          {t("billing.invoices_title")}
        </Title>
        <FormError message={error} />
        {invoices.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : invoices.isError ? (
          <FormError message={errorMessage(invoices.error, t("error.generic"))} />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="invoices-empty">
            {t("billing.invoices_empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="sm" aria-label={t("billing.invoices_title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("billing.invoice_number")}</Table.Th>
                  <Table.Th>{t("billing.period")}</Table.Th>
                  <Table.Th ta="right">{t("billing.invoice_total")}</Table.Th>
                  <Table.Th ta="right">{t("billing.invoice_paid")}</Table.Th>
                  <Table.Th>{t("billing.invoice_status")}</Table.Th>
                  <Table.Th>{t("billing.due")}</Table.Th>
                  <Table.Th>{t("staff.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((inv) => (
                  <Table.Tr key={inv.id} data-testid="invoice-row">
                    <Table.Td>
                      <Text size="sm" fw={600} ff="monospace">
                        {inv.number}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {t("billing.invoice_calls", { count: inv.usage_count })}
                      </Text>
                    </Table.Td>
                    <Table.Td>{period(inv)}</Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{f.money(inv.total_minor, inv.currency)}</Text>
                      <Text size="xs" c="dimmed">
                        {t("billing.incl_vat", { vat: f.money(inv.vat_minor, inv.currency) })}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{f.money(inv.amount_paid_minor, inv.currency)}</Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge color={INVOICE_COLOUR[inv.status]} data-testid="invoice-status">
                          {t(`invoice_status.${inv.status}`)}
                        </Badge>
                        {inv.blocked && (
                          <Badge color="red" variant="outline" data-testid="invoice-blocked">
                            {t("billing.invoice_blocked")}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {inv.status === "paid" && inv.paid_at
                        ? t("billing.paid_on", { date: f.date(inv.paid_at) })
                        : f.date(inv.due_at)}
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        loading={busy === inv.id}
                        onClick={() => void download(inv)}
                        aria-label={t("billing.download_pdf_for", { number: inv.number })}
                      >
                        {t("billing.download_pdf")}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}
